<template>
  <div
    :class="styleClass"
    :style="{ 'max-width': $vuetify.breakpoint.lgAndUp ? width : '100%' }"
  >
    <div class="date-container">
      <div class="date-input day-input">
        <BaseInput
          vid="dayInput"
          ref="dayInput"
          hideDetails
          placeholder="DD"
          type="text"
          mask="##"
          v-model="day"
          labelClass="font-16 black-color"
          :dataUiid="`${dataUiid}_Day`"
          :hideValidationIcon="false"
          :label="$vuetify.breakpoint.lgAndUp ? 'Day (DD)' : 'Day'"
          ariaLabel="Day"
          :rules="dayRules"
          :height="height"
          pattern="\d*"
          inputmode="numeric"
          @blur="onBlur"
          @keydown="onKeyDown"
        />
      </div>
      <div class="date-input month-input">
        <BaseInput
          vid="monthInput"
          ref="monthInput"
          hideDetails
          placeholder="MM"
          type="text"
          mask="##"
          v-model="month"
          labelClass="font-16 black-color"
          :dataUiid="`${dataUiid}_Month`"
          :hideValidationIcon="false"
          :label="$vuetify.breakpoint.lgAndUp ? 'Month (MM)' : 'Month'"
          ariaLabel="Month"
          :rules="monthRules"
          :height="height"
          @blur="onBlur"
          @keydown="onKeyDown"
          pattern="\d*"
          inputmode="numeric"
        />
      </div>
      <div class="date-input year-input">
        <BaseInput
          vid="yearInput"
          ref="yearInput"
          hideDetails
          placeholder="YYYY"
          type="text"
          mask="####"
          v-model="year"
          labelClass="font-16 black-color"
          :dataUiid="`${dataUiid}_Year`"
          :hideValidationIcon="false"
          :rules="yearRules"
          :label="$vuetify.breakpoint.lgAndUp ? 'Year (YYYY)' : 'Year'"
          ariaLabel="Year"
          :height="height"
          error-icon="mdi mdi-alert-circle"
          @blur="onBlur"
          @keydown="onKeyDown"
          pattern="\d*"
          inputmode="numeric"
        />
      </div>
    </div>
    <div class="invisible-input">
      <BaseInput
        :vid="vid"
        ref="dateInput"
        :hideDetails="hideDetails"
        :dataUiid="dataUiid"
        v-model="innerValue"
        :rules="rules"
      />
    </div>
  </div>
</template>
<script>
import { addFieldLabel, formatTwoDigitNumber } from "@/common/utils";
import BaseInput from "@/components/BaseComponents/BaseInput.vue";

import moment from "moment";

export default {
  name: "BaseInputDate",
  components: {
    BaseInput,
  },
  props: {
    vid: String,
    value: {
      type: String,
      required: false,
    },
    labelClass: {
      type: String,
      default: null,
    },
    label: {
      type: String,
    },
    width: {
      type: [String, Number],
      required: false,
      default: "440px",
    },
    height: {
      type: [String, Number],
      required: false,
    },
    helpText: {
      type: String,
      default: null,
    },
    rules: {
      type: Object,
      required: true,
    },
    styleClass: {
      type: String,
      required: false,
    },
    dataUiid: {
      type: String,
    },
    externalErrorMsg: {
      type: String,
      default: null,
    },
    hideDetails: {
      type: [Boolean, String],
    },
  },
  watch: {
    day(value) {
      this.assignDateValue();
      if (!value) {
        this.resetField("dayInput");
      }
      this.focusNext(value, "month");
    },
    month(value) {
      this.assignDateValue();
      if (!value) {
        this.resetField("monthInput");
      }
      this.focusNext(value, "year");
    },
    year(value) {
      this.assignDateValue();
      if (!value) {
        this.resetField("yearInput");
      }
    },
    // Handles internal model changes.
    innerValue(value) {
      this.$emit("input", value, this.fieldName, this.$refs.provider);
    },
    // Handles external model changes.
    value(val) {
      if (val !== this.innerValue) {
        this.innerValue = val;
      }
    },
  },
  computed: {
    monthInputElement() {
      return this.$el.querySelector(`[Data-UIID="${this.dataUiid}_Month"`);
    },
    yearInputElement() {
      return this.$el.querySelector(`[Data-UIID="${this.dataUiid}_Year"`);
    },
  },
  data() {
    return {
      innerValue: "",
      skipFocusing: true,
      day: "",
      dayRules: addFieldLabel(
        {
          number_in_range: {
            min: 1,
            max: 31,
          },
        },
        "Day"
      ),
      month: "",
      monthRules: addFieldLabel(
        {
          number_in_range: {
            min: 1,
            max: 12,
          },
        },
        "Month"
      ),
      year: "",
      yearRules: addFieldLabel(
        {
          min_length: {
            length: 4,
          },
          number_in_range: {
            min: 1000,
            max: 9999,
          },
        },
        "Year"
      ),
    };
  },
  methods: {
    focusNext(value, nextInputToFocus) {
      if (!value || value.length < 2) {
        return;
      }
      const setInputFocus = {
        month: () => {
          if (this.skipFocusing) {
            return;
          }
          this.monthInputElement?.focus();
        },
        year: () => {
          if (this.skipFocusing) {
            this.skipFocusing = false;
            return;
          }
          this.yearInputElement?.focus();
        },
      };

      if (setInputFocus[nextInputToFocus]) setInputFocus[nextInputToFocus]();
    },
    resetField(field) {
      this.$refs[field].$refs.provider.reset();
    },
    resetFieldAll() {
      this.day = "";
      this.month = "";
      this.year = "";
      this.resetField("dayInput");
      this.resetField("monthInput");
      this.resetField("yearInput");
    },
    onBlur(value) {
      setTimeout(() => {
        const dateFieldHasFocus = [
          "dayInput",
          "monthInput",
          "yearInput",
        ].includes(document.activeElement.id);
        if (
          (!value && !this.innerValue) ||
          this.innerValue ||
          !dateFieldHasFocus
        ) {
          this.$refs.dateInput.$refs.provider.validate();
        }
      }, 0);
    },
    assignDateValue() {
      let invalidDay = !!this.day && (this.day < 1 || this.day > 31);
      let invalidMonth = !!this.month && (this.month < 1 || this.month > 12);
      let invalidYear = !!this.year && this.year < 1000;
      const notHasInvalidDate = !(invalidDay || invalidMonth || invalidYear);
      const missingData = !(this.day && this.month && this.year);

      if (notHasInvalidDate && missingData) {
        this.innerValue = "";
      } else {
        this.innerValue = `${formatTwoDigitNumber(
          this.day
        )}/${formatTwoDigitNumber(this.month)}/${invalidYear ? "" : this.year}`;
      }
    },
    onKeyDown(event) {
      const invalidChars = ["e", "E", "+", "-", "."];
      if (invalidChars.includes(event.key)) {
        event.preventDefault();
      }
    },
  },
  beforeMount() {
    if (this.rules["required"]) {
      this.dayRules.required = {
        fieldLabel: "Day (DD)",
        required: true,
      };
      this.monthRules.required = {
        fieldLabel: "Month (MM)",
        required: true,
      };
      this.yearRules.required = {
        fieldLabel: "Year (YYYY)",
        required: true,
      };
    }
    const date = moment(
      this.value,
      ["DD/MM/YYYY HH:mm:ss", "DD/MM/YYYY"],
      true
    );
    if (this.value && date.isValid()) {
      this.day = formatTwoDigitNumber(date.date().toString());
      this.month = formatTwoDigitNumber((date.month() + 1).toString());
      this.year = date.year().toString();
      this.innerValue = `${formatTwoDigitNumber(
        this.day
      )}/${formatTwoDigitNumber(this.month)}/${this.year}`;
    } else {
      this.innerValue = "";
      this.skipFocusing = false;
    }
  },
};
</script>
<style lang="scss" scoped>
.date-container {
  display: flex;
  justify-content: space-between;
}
.form__control {
  margin-bottom: 4px;
}
.date-input {
  text-wrap: nowrap;
  :deep(.v-input) {
    .v-input__control {
      .v-input__slot {
        .v-text-field__slot {
          input {
            text-align: center;
          }
        }
      }
    }
  }
}
.invisible-input {
  padding-top: 0;
  padding-bottom: 0;
  :deep(.v-input) {
    padding-top: 0;
    margin-top: 0;
    .v-input__control {
      .v-input__slot {
        display: none;
      }
    }
  }
}

.date-input {
  padding: 0 0;

  :deep(.col-12.col-md-6.pa-0) {
    max-width: 100%;
  }

  &.day-input {
    max-width: calc(30% - 12px);
    :deep(.v-input) {
      margin-bottom: 0 !important;
    }
    p {
      font-size: 14px !important;
    }
  }

  &.month-input {
    max-width: calc(30% - 12px);
    :deep(.v-input) {
      margin-bottom: 0 !important;
    }
    p {
      font-size: 14px !important;
    }
  }

  &.year-input {
    max-width: calc(40% - 16px);
    :deep(.v-input) {
      margin-bottom: 0 !important;
    }
    p {
      font-size: 14px !important;
    }
  }
}
.policy-start-date {
  margin-top: -2px !important;
  margin-bottom: 5px !important;
}
.sub-section {
  margin-bottom: -26px;
}
.mortgage-completion-date {
  margin-top: -3px !important;
}
@media screen and (max-width: 992px) {
  .sub-section {
    margin-bottom: -10px;
  }
}
@media screen and(max-width: ($grid-breakpoints-sm - 1)) {
  .date-input {
    &.day-input {
      max-width: calc(30% - 11px);
    }

    &.month-input {
      max-width: calc(30% - 11px);
    }

    &.year-input {
      max-width: calc(40% - 15px);
    }
  }
}
</style>
